import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Lightbulb, CheckCircle } from 'lucide-react';
import { placementGuide } from '../data/plantsData';

const PlacementGuide = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="placement" className="py-20 bg-gray-50 text-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-center mb-4">
            <MapPin className="h-8 w-8 text-primary-500 mr-3" />
            <h2 className="text-4xl font-bold text-gray-900">
              Perfect Plant Placement
            </h2>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover the ideal spots in your home for different plants. The right placement can make all the difference in your plant's health and growth.
          </p>
        </motion.div>

        <motion.div
          className="space-y-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {placementGuide.map((room, index) => (
            <motion.div
              key={room.room}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } items-center gap-8 lg:gap-12`}
              variants={cardVariants}
            >
              {/* Image */}
              <div className="flex-1 w-full">
                <motion.div
                  className="relative overflow-hidden rounded-2xl shadow-lg group border border-gray-200"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.4 }}
                >
                  <img
                    src={room.image}
                    alt={`${room.room} plants`}
                    className="w-full h-80 lg:h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="flex-1 w-full">
                <motion.div
                  className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow"
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {room.room}
                  </h3>
                  <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                    {room.description}
                  </p>

                  {/* Recommended Plants */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary-500 mr-2" />
                      Recommended Plants:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {room.plants.map((plant) => (
                        <motion.span
                          key={plant}
                          className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium border border-primary-200"
                          whileHover={{ scale: 1.05, backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {plant}
                        </motion.span>
                      ))}
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                      <Lightbulb className="h-5 w-5 text-yellow-500 mr-2" />
                      Pro Tips:
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {room.tips}
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="text-center mt-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <motion.button
            className="group bg-primary-600 hover:bg-primary-700 text-white border border-primary-600 hover:border-primary-700 px-8 py-3 rounded-full font-semibold transition-all duration-300 flex items-center space-x-2 mx-auto transform hover:scale-105 shadow-md hover:shadow-lg"
            whileTap={{ scale: 0.95 }}
          >
            <span>Get Personalized Placement Advice</span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default PlacementGuide;
